import { setCookie, getCookie } from "../../../components-shared/utils/cookie.js";
import { CheckSelect } from "../../common/check-select.js";
import { FolderApi } from "../../api/archive/folder/folder-api.js";
import { CategoryApi } from "../../api/archive/category/category-api.js";
import { IDocumentHistoryConfig } from "./document-history.js";
import { ArchiveType } from "../../../components-shared/enums/archive-type.js";
import { standardToExtendedApiReporter } from "../../../components-shared/utils/notify-error-reporter.js";
import { IItemTreeItem, IItemTreeSelection } from "../../../components-shared/elements/item-tree/item-tree-item.js";
import { IFolder } from "../../api/archive/folder/folder.js";
import { ICategory } from "../../api/archive/category/category.js";
import { CheckSelectTags } from "../../common/check-select-tags.js";
import { IKeyParentValueSelectionMode } from "../../../components-shared/interfaces/key-value.js";

export class DocumentHistoryFilters extends HTMLElement {
    constructor(private config: IDocumentHistoryConfig, private labels: IDocumentHistoryFilterLabels) {
        super();

        this.folderApi = new FolderApi(standardToExtendedApiReporter(this.config.errorReporter));
        this.categoryApi = new CategoryApi(standardToExtendedApiReporter(this.config.errorReporter));
        this.loadFromCookies();
    }

    /* #region Public */
    public values: IDocumentHistoryFilterValues;
    public onFilterChanged: (values: IDocumentHistoryFilterValues) => void;

    /* #endregion */

    /* #region Event handlers */
    private searchInputTimeoutToken: number;

    private onSearchInputKeyUp(_: KeyboardEvent) {
        clearTimeout(this.searchInputTimeoutToken);
        this.searchInputTimeoutToken = setTimeout(this.searchTermChanged.bind(this), 500) as unknown as number;
    }

    private searchTermChanged() {
        this.values.searchTerm = this.searchInput.value;
        this.saveToCookies();

        if (this.onFilterChanged) {
            this.onFilterChanged(this.values);
        }
    }

    /*
    private fromDateChanged() {
        this.values.fromDate = this.fromDateInput.valueAsDate;
        this.saveToCookies();

        if (this.onFilterChanged) {
            this.onFilterChanged(this.values);
        }
    }*/

    private groupsChanged(selectedGroups: IKeyParentValueSelectionMode<string, string, string>[]) {
        this.values.selectedGroups = selectedGroups;
        this.saveToCookies();

        if (this.onFilterChanged) {
            this.onFilterChanged(this.values);
        }
    }

    private tagsChanged(selectedTags: IKeyParentValueSelectionMode<string, string, string>[]) {
        this.values.selectedTags = selectedTags;
        this.saveToCookies();

        if (this.onFilterChanged) {
            this.onFilterChanged(this.values);
        }
    }
    /* #endregion */

    /* #region Private */

    private folderApi: FolderApi;
    private categoryApi: CategoryApi;

    private isInitialized: boolean = false;
    //private fromDateInput: HTMLInputElement;
    private searchInput: HTMLInputElement;
    private groupsSelect: CheckSelect;
    private tagsSelect: CheckSelect;
    private groupsSelectTags: CheckSelectTags;
    private tagsSelectTags: CheckSelectTags;

    private async initialize() {
        this.loadFromCookies();

        const controlsContainer = document.createElement("div");
        controlsContainer.classList.add("filters-controls-container");

        this.appendChild(controlsContainer);

        // Search input
        this.searchInput = document.createElement("input");
        this.searchInput.type = "text";
        this.searchInput.placeholder = this.labels.searchInArticles;
        this.searchInput.classList.add("search-input");
        this.searchInput.value = this.values.searchTerm;

        controlsContainer.appendChild(this.searchInput);

        this.searchInput.onkeyup = this.onSearchInputKeyUp.bind(this);
        this.searchInput.onkeydown = (e: KeyboardEvent) => { if (e.key === "Enter") { e.preventDefault(); } };

        // Groups selector (tags)
        this.groupsSelectTags = new CheckSelectTags(this.labels.groups);

        // Tags selector (tags)
        this.tagsSelectTags = new CheckSelectTags(this.labels.tags);

        // Groups filter selector
        this.groupsSelect = new CheckSelect(this.labels.groupFilter, items => { this.groupsChanged(items); }, null, null, this.groupsSelectTags);
        this.groupsSelect.setSelectedItems(this.values.selectedGroups);
        controlsContainer.appendChild(this.groupsSelect);

        // Tags filter selector
        this.tagsSelect = new CheckSelect(this.labels.tagFilter, items => { this.tagsChanged(items); }, null, null, this.tagsSelectTags);
        this.tagsSelect.setSelectedItems(this.values.selectedTags);
        controlsContainer.appendChild(this.tagsSelect);

        const tagsContainer = document.createElement("div");
        tagsContainer.classList.add("filters-tags-container");

        this.appendChild(tagsContainer);

        tagsContainer.appendChild(this.groupsSelectTags);
        tagsContainer.appendChild(this.tagsSelectTags);

        // From date input
        /*this.fromDateInput = document.createElement("input");
        this.fromDateInput.type = "date";
        this.fromDateInput.valueAsDate = this.values.fromDate;

        this.appendChild(this.fromDateInput);

        this.fromDateInput.onchange = this.fromDateChanged.bind(this);*/

        // Load groups and tags
        const groupsFiltersPromise = this.folderApi.getChildren(this.config.rootFolderId, ArchiveType.Document, true);
        const tagsFiltersPromise = this.categoryApi.getChildren(this.config.tagCategoriesRootId, ArchiveType.Document);

        const [groups, tags] = await Promise.all([groupsFiltersPromise, tagsFiltersPromise]);

        // Update groups and tags with data from server
        if (!!groups && groups) {
            this.groupsSelect.setItems(groups.filter(g1 => !groups.map(g2 => g2.folderId).includes(g1.parentId)).map(x => this.folderToItemTreeItem(groups, x)));
        }

        if (!!tags && tags.length) {
            this.tagsSelect.setItems(tags.filter(t1 => !tags.map(t2 => t2.categoryId).includes(t1.parentId)).map(x => this.categoryToItemTreeItem(tags, x)));
        }
    }

    private folderToItemTreeItem(folders: IFolder[], folder: IFolder): IItemTreeItem {
        const children = folders.filter(x => x.parentId === folder.folderId);

        return {
            value: folder.folderId.toString(),
            children: children.map(x => this.folderToItemTreeItem(folders, x)),
            text: folder.name
        } as IItemTreeItem;
    }

    private categoryToItemTreeItem(categories: ICategory[], category: ICategory): IItemTreeItem {
        const children = categories.filter(x => x.parentId === category.categoryId);

        return {
            value: category.categoryId.toString(),
            children: children.map(x => this.categoryToItemTreeItem(categories, x)),
            text: category.name
        } as IItemTreeItem;
    }

    private loadFromCookies() {
        const oneMonthAgo = new Date();
        oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);

        const selectedGroupsJson = getCookie("doc-history-groups");
        const selectedGroups = selectedGroupsJson && selectedGroupsJson.startsWith("[") ? JSON.parse(selectedGroupsJson) : [];

        const selectedTagsJson = getCookie("doc-history-tags");
        const selectedTags = selectedTagsJson && selectedTagsJson.startsWith("[") ? JSON.parse(selectedTagsJson) : [];

        this.values = {
            searchTerm: getCookie("doc-history-search") || "",
            //fromDate: new Date(getCookie("doc-history-fromdate") || oneMonthAgo.toISOString()),
            selectedGroups: selectedGroups,
            selectedTags: selectedTags
        };
    }

    private saveToCookies() {
        setCookie("doc-history-search", this.values.searchTerm);
        //setCookie("doc-history-fromdate", this.values.fromDate.toISOString());
        setCookie("doc-history-groups", JSON.stringify(this.values.selectedGroups));
        setCookie("doc-history-tags", JSON.stringify(this.values.selectedTags));
    }

    /* #endregion */

    async connectedCallback() {
        if (!this.isInitialized) {
            this.isInitialized = true;
            await this.initialize();
        }
    }
}

export interface IDocumentHistoryFilterValues {
    searchTerm: string;
    fromDate?: Date;
    selectedGroups: IKeyParentValueSelectionMode<string, string, string>[];
    selectedTags: IKeyParentValueSelectionMode<string, string, string>[];
}

export interface IDocumentHistoryFilterLabels {
    searchInArticles: string;
    groupFilter: string;
    groups: string;
    tagFilter: string;
    tags: string;
}

customElements.define("document-history-filters", DocumentHistoryFilters);